import { lazy, Suspense, useState, type ComponentProps } from "react";
import { useQuery } from "@tanstack/react-query";
import { useActualViewer } from "@softmax/web-features/session/records";
import { gameContextQueryOptions } from "@/features/game/records";
import { useGameRoute } from "@/features/game/navigation";
import type { SessionScope } from "@softmax/ide/workspace-client";
import { defaultLeagueId } from "@softmax/ide/league-catalog";
import { useCogentName, useCogentSession } from "@softmax/cogent-sdk/react";
import { handedOff, softmaxSite } from "../onepager/cogent-site";
import { widgetChatView } from "../onepager/widget-chat-view";
import type { PageContext } from "./chat/chat-view";
import { ClientOnly } from "@tanstack/react-router";
import { CogentWidget } from "./cogent-widget";
import type { CogentAdapter } from "./widget-types";
const LazyCogentOrb = lazy(() =>
  import("./orb/cogent-orb").then((module) => ({
    default: module.CogentOrb,
  })),
);
function CogentCharacter(props: ComponentProps<typeof LazyCogentOrb>) {
  return (
    <ClientOnly>
      <Suspense fallback={null}>
        <LazyCogentOrb {...props} />
      </Suspense>
    </ClientOnly>
  );
}
const adapter: CogentAdapter = { loadLook: async () => null };
export function CogentPanel() {
  const viewer = useActualViewer();
  const { href, isGameRoute } = useGameRoute();
  const pathname = href.split("?", 1)[0]!;
  const options = gameContextQueryOptions(href);
  const game = useQuery({
    ...options,
    enabled: isGameRoute,
    throwOnError: false,
  });
  const context =
    isGameRoute && game.data?.kind === "ready" ? game.data.game : null;
  const scope: SessionScope =
    context?.coworldId && context.league
      ? {
          scope: "coworld",
          coworld: context.coworldId,
          league: context.league.id,
        }
      : { scope: "workspace", coworld: null, league: null };
  const page: PageContext = {
    coworld: context?.coworldName ?? "Play",
    path: pathname,
    ...(context?.league ? { leagueId: context.league.id } : {}),
    seat: "here",
    program: null,
    rank: null,
    round: null,
    roundState: null,
  };

  if (isGameRoute && (!context?.league || !context.coworldId || game.error))
    return null;

  return (
    <ClientOnly>
      <ConnectedPanel
        key={`${viewer.data?.subject_id ?? "anonymous"}:${scope.coworld ?? "workspace"}:${scope.league ?? ""}`}
        scope={scope}
        viewerId={viewer.data?.subject_id ?? null}
        page={page}
      />
    </ClientOnly>
  );
}
function ConnectedPanel({
  viewerId,
  page,
  scope,
}: {
  viewerId: string | null;
  page: PageContext;
  scope: SessionScope;
}) {
  const leagueId = page.leagueId ?? defaultLeagueId;
  const sessionKey = viewerId ? `${viewerId}:${leagueId}` : null;
  const [active, setActive] = useState(false);
  const cogentName = useCogentName(softmaxSite.id, viewerId);
  const session = useCogentSession({
    site: softmaxSite,
    page: {
      label: page.coworld,
      path: page.path,
      league: leagueId,
    },
    enabled: active && viewerId !== null,
    sessionKey,
    viewerId,
    coworldId: scope.coworld,
    leagueId,
    followFromStart: sessionKey !== null && handedOff.delete(sessionKey),
  });
  return (
    <CogentWidget
      live={
        (active || session.following) && viewerId
          ? widgetChatView(session)
          : null
      }
      cogent={
        viewerId
          ? {
              id: viewerId,
              name: cogentName ?? "Cogent",
              sendable: true,
              state: "here",
              doing: null,
              program: null,
              lastActive: null,
            }
          : null
      }
      signedIn={viewerId !== null}
      onVisibilityChange={setActive}
      coworld={{ name: page.coworld, joinHref: null }}
      releasable={[]}
      canRelease={false}
      adapter={adapter}
      page={page}
      character={CogentCharacter}
      assetsBaseUrl="/cogent/chargen/"
    />
  );
}
